import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Point } from '../types/Point';

import { Estimate } from '../api/protosCompiled/projectEstimate/projectEstimate_pb';
import { Contract } from '../api/protosCompiled/contract/contract_pb';
import { ChangeOrder } from '../api/protosCompiled/changeOrder/changeOrder_pb';
import { Project } from '../api/protosCompiled/project/project_pb';
import { Object as ProtoObject } from '../api/protosCompiled/object/object_pb';
import { BreakOut, TakeoffAssembly } from '../types/AssemblyItemType';

export interface AvailableObject {
  id: string;
  project_id: string;
  objectName: string;
  gcsObjectName: string;
  userScale: number;
}

export const defaultContract: Contract.AsObject = {
  contractid: '',
  customid: '',
  projectid: '',
  datecreated: '',
  lastupdated: '',
  contractname: '',
  contractdescription: '',
  associatedestimatesList: [],
};

export const defaultProject: Project.AsObject = {
  id: '',
  name: '',
  address: '',
  description: '',
  startdate: '',
  enddate: '',
  datecreated: '',
  openaivectorstoreid: '',
  openaiprojectassistantid: '',
  createdbyuserid: '',
  createdbyaccountid: '',
  projectcontractsList: [],
  projecthierarchyid: '',
};

export const angleOptionMultipliers = [1, 5, 15, 45];
const defaultAngleOptions = Array.from(
  { length: 360 / angleOptionMultipliers[5] },
  (_, i) => i * angleOptionMultipliers[5]
);

// interface SelectedPoint {
//   pointId: string;
//   assemblyId: string;
//   originalX: number;
//   originalY: number;
// }

// interface SelectedPointsData {
//   selectedPoints: SelectedPoint[];
//   hasCopiedItems: boolean;
// }

export type EstimateStore = {
  isHelpOpen: boolean;
  setIsHelpOpen: (isHelpOpen: boolean) => void;

  isAddingVertical: boolean;
  setIsAddingVertical: (isAddingVertical: boolean) => void;

  breakoutFilterObject: BreakOut | null;
  setBreakoutFilterObject: (breakoutFilterObject: BreakOut | null) => void;

  selectedBreakout: BreakOut | null;
  setSelectedBreakout: (selectedBreakout: BreakOut | null) => void;

  angleOptions: number[];
  setAngleOptions: (angleOptions: number[]) => void;

  isLoggedIn: boolean;
  setIsLoggedIn: (isLoggedIn: boolean) => void;

  selectedProject: Project.AsObject;
  setSelectedProject: (selectedProject: Project.AsObject) => void;

  selectedEstimate: Estimate.AsObject | null;
  setSelectedEstimate: (estimate: Estimate.AsObject | null) => void;

  selectedContract: Contract.AsObject | null;
  setSelectedContract: (contract: Contract.AsObject | null) => void;

  selectedChangeOrder: ChangeOrder.AsObject | null;
  setSelectedChangeOrder: (changeOrder: ChangeOrder.AsObject | null) => void;

  selectedFilter: string;
  setSelectedFilter: (filter: string) => void;

  projectListIsMounted: boolean;
  setProjectListIsMounted: (isMounted: boolean) => void;

  showSidebarMenu: boolean;
  setShowSidebarMenu: (show: boolean) => void;

  isViewingProjectDashboard: boolean;
  setIsViewingProjectDashboard: (isViewingProjectDashboard: boolean) => void;

  isCreatingEstimate: boolean;
  setIsCreatingEstimate: (isCreatingEstimate: boolean) => void;

  isViewingEstimateForm: boolean;
  setIsViewingEstimateForm: (isViewingEstimateForm: boolean) => void;

  isViewingEstimateList: boolean;
  setIsViewingEstimateList: (isViewingEstimateList: boolean) => void;

  isViewingChangeOrderList: boolean;
  setIsViewingChangeOrderList: (isViewingChangeOrderList: boolean) => void;

  isViewingContractForm: boolean;
  setIsViewingContractForm: (isViewingContractForm: boolean) => void;

  isCreatingChangeOrder: boolean;
  setIsCreatingChangeOrder: (isCreatingChangeOrder: boolean) => void;

  isViewingChangeOrderForm: boolean;
  setIsViewingChangeOrderForm: (isViewingChangeOrderForm: boolean) => void;

  isCreatingContract: boolean;
  setIsCreatingContract: (isCreatingContract: boolean) => void;

  isViewingContractList: boolean;
  setIsViewingContractList: (isViewingContractList: boolean) => void;

  isViewingExtension: boolean;
  setIsViewingExtension: (isViewingExtension: boolean) => void;

  isViewingAllocation: boolean;
  setIsViewingAllocation: (isViewingAllocation: boolean) => void;

  //--------------------------------------------------------------------------
  //pointEditor state to trigger updates in other components
  isViewingColorEditor: boolean;
  setIsViewingColorEditor: (isViewingColorEditor: boolean) => void;

  showLineMeasurements: boolean;
  setShowLineMeasurements: (showLineMeasurements: boolean) => void;

  showPointMeasurements: boolean;
  setShowPointMeasurements: (showPointMeasurements: boolean) => void;

  includeAllPoints: boolean;
  setIncludeAllPoints: (includeAllPoints: boolean) => void;

  // Tracks if user interacted with PointEditor since it opened
  hasPointEditorInteraction: boolean;
  setHasPointEditorInteraction: (hasInteracted: boolean) => void;

  // Tracks if a run has been started via Add to Run in current session
  hasActiveRunStarted: boolean;
  setHasActiveRunStarted: (started: boolean) => void;

  // Tracks if an active run is in progress (persists through editor openings)
  hasActiveRun: boolean;
  setHasActiveRun: (active: boolean) => void;

  isInsertingPoint: boolean;
  setIsInsertingPoint: (isInsertingPoint: boolean) => void;

  isEditingPointFillColor: boolean;
  isEditingPointBorderColor: boolean;
  isEditingLineColor: boolean;
  setIsEditingPointStyle: (params: {
    isEditingPointFillColor?: boolean;
    isEditingPointBorderColor?: boolean;
    isEditingLineColor?: boolean;
  }) => void;

  backgroundColor: string;
  setBackgroundColor: (color: string) => void;

  //-----------------------------------------------------------

  numPages: number;
  setNumPages: (numPages: number) => void;

  currentPage: number;
  setCurrentPage: (currentPage: number) => void;

  isOpeningViewport: boolean;
  setIsOpeningViewport: (isOpeningViewport: boolean) => void;

  selectedObject: ProtoObject.AsObject | null;
  setSelectedObject: (object: ProtoObject.AsObject | null) => void;

  isCreatingNewProject: boolean;
  setIsCreatingNewProject: (creatingNewProject: boolean) => void;

  //--------------------------------------------------------------------------

  pdfDimensions: { width: number; height: number };
  setPdfDimensions: (dimensions: { width: number; height: number }) => void;

  //Tool bar states for the document viewer------------------------------------
  userScale: number;
  setUserScale: (scale: number) => void;

  // Lasso selection mode ------------------------------------------------------
  isLassoMode: boolean;
  setIsLassoMode: (on: boolean) => void;
  isLassoDrawing: boolean;
  setIsLassoDrawing: (on: boolean) => void;
  lassoPoints: { x: number; y: number }[];
  setLassoPoints: (
    points:
      | { x: number; y: number }[]
      | ((prev: { x: number; y: number }[]) => { x: number; y: number }[])
  ) => void;

  measuredPoints: { x: number; y: number }[];
  setMeasuredPoints: (
    points:
      | { x: number; y: number }[]
      | ((prevPoints: { x: number; y: number }[]) => { x: number; y: number }[])
  ) => void;

  temporaryPoints: Point[];
  setTemporaryPoints: (points: Point[] | ((prevPoints: Point[]) => Point[])) => void;

  isAnnotatingTakeoff: boolean;
  setIsAnnotatingTakeoff: (isAnnotatingTakeoff: boolean) => void;

  //Assembly window states-----------------------------------------------------

  showAssemblyWindow: boolean;
  setShowAssemblyWindow: (show: boolean) => void;

  pointIsSelected: boolean;
  setPointIsSelected: (pointIsSelected: boolean) => void;

  selectedTotalLength: number; // use for temporary storage of the length of the selected assembly takeff, reset to null after use
  setSelectedTotalLength: (length: number) => void;

  isCreatingTakeoffAssembly: boolean; // specific to performing a takeoff with an assembly
  setIsCreatingTakeoffAssembly: (isCreatingTakeoffAssembly: boolean) => void;

  isUpdatingTakeoffAssembly: boolean; // specific to updating a takeoff
  setIsUpdatingTakeoffAssembly: (isUpdatingTakeoffAssembly: boolean) => void;

  isCounting: boolean; // for the specific  count item selection of the Assembly Manager
  setIsCounting: (isCounting: boolean) => void;

  isGettingLength: boolean; // for the specific length item selection of the Assembly Manager
  setIsGettingLength: (isGettingLength: boolean) => void;

  isGettingArea: boolean; // for the specific area item selection of the Assembly Manager
  setIsGettingArea: (isGettingArea: boolean) => void;

  //--------------------------------------------------------------------------

  cursorPosition: { x: number; y: number };
  setCursorPosition: (position: { x: number; y: number }) => void;

  isDragging: boolean;
  setIsDragging: (isDragging: boolean) => void;

  // newAnnotations: TakeoffAssemblies[] | null;
  // setNewAnnotations: (newAnnotations: TakeoffAssemblies[] | null) => void;

  isEditingPoint: boolean;
  isEditingStyle: boolean;
  isDeletingPoint: boolean;
  assemblyIndex: number | null;
  pointIndex: number;
  lineIndex: number;
  editingPointX: number | null;
  editingPointY: number | null;

  setIsEditingPoint: (params: {
    isEditingPoint: boolean;
    isViewingTakeoffEditor: boolean;
    isEditingStyle?: boolean;
    assemblyIndex?: number | null;
    pointIndex?: number | null;
    lineIndex?: number | null;
    pointsList?: TakeoffAssembly['pointsList'];
    x?: number | null;
    y?: number | null;
  }) => void;

  // updatePoint: (params: {
  //   assemblyIndex: number;
  //   pointIndex: number;
  //   newPosition?: { x: number; y: number } | null;
  //   isDeletingPoint?: boolean;
  // }) => void;

  // selectedPointsData: SelectedPointsData;

  selectedEstimateIdForChangeOrder: string | null;
  setSelectedEstimateIdForChangeOrder: (id: string | null) => void;

  // Project Hierarchy state
  expandedHierarchyNodes: Set<string>;
  setExpandedHierarchyNodes: (nodes: Set<string>) => void;
  toggleHierarchyNode: (nodeId: string) => void;
  clearExpandedHierarchyNodes: () => void;
};

export const useUnityBuildStore = create<EstimateStore>()(
  persist(
    (set, get) => ({
      isHelpOpen: false,
      setIsHelpOpen: isHelpOpen => set({ isHelpOpen }),

      isAddingVertical: false,
      setIsAddingVertical: isAddingVertical => set({ isAddingVertical }),

      breakoutFilterObject: null,
      setBreakoutFilterObject: breakoutFilterObject =>
        set({ breakoutFilterObject: breakoutFilterObject ?? null }),

      selectedBreakout: null,
      setSelectedBreakout: selectedBreakout => set({ selectedBreakout: selectedBreakout ?? null }),

      angleOptions: defaultAngleOptions,
      setAngleOptions: angleOptions => set({ angleOptions: angleOptions ?? defaultAngleOptions }),

      isLoggedIn: false,
      setIsLoggedIn: isLoggedIn => set({ isLoggedIn }),

      selectedProject: defaultProject,
      setSelectedProject: selectedProject => set({ selectedProject: selectedProject }),

      selectedEstimate: null,
      setSelectedEstimate: estimate => set({ selectedEstimate: estimate }),

      selectedContract: null,
      setSelectedContract: contract => set({ selectedContract: contract }),

      selectedChangeOrder: null,
      setSelectedChangeOrder: changeOrder => set({ selectedChangeOrder: changeOrder }),

      selectedFilter: 'all',
      setSelectedFilter: filter => set({ selectedFilter: filter }),

      projectListIsMounted: false,
      setProjectListIsMounted: isMounted => set({ projectListIsMounted: isMounted }),

      showSidebarMenu: false,
      setShowSidebarMenu: show => set({ showSidebarMenu: show }),

      isViewingProjectDashboard: true,
      setIsViewingProjectDashboard: isViewingProjectDashboard =>
        set({ isViewingProjectDashboard: isViewingProjectDashboard }),

      isCreatingEstimate: false,
      setIsCreatingEstimate: isCreatingEstimate => set({ isCreatingEstimate: isCreatingEstimate }),

      isViewingEstimateForm: false,
      setIsViewingEstimateForm: isViewingEstimateForm =>
        set({ isViewingEstimateForm: isViewingEstimateForm }),

      isViewingEstimateList: false,
      setIsViewingEstimateList: isViewingEstimateList =>
        set({ isViewingEstimateList: isViewingEstimateList }),

      isViewingChangeOrderList: false,
      setIsViewingChangeOrderList: isViewingChangeOrderList =>
        set({ isViewingChangeOrderList: isViewingChangeOrderList }),

      isViewingContractForm: false,
      setIsViewingContractForm: isViewingContractForm =>
        set({ isViewingContractForm: isViewingContractForm }),

      isCreatingChangeOrder: false,
      setIsCreatingChangeOrder: isCreatingChangeOrder =>
        set({ isCreatingChangeOrder: isCreatingChangeOrder }),

      isViewingChangeOrderForm: false,
      setIsViewingChangeOrderForm: isViewingChangeOrderForm =>
        set({ isViewingChangeOrderForm: isViewingChangeOrderForm }),

      isCreatingContract: false,
      setIsCreatingContract: isCreatingContract => set({ isCreatingContract: isCreatingContract }),

      isViewingContractList: false,
      setIsViewingContractList: isViewingContractList =>
        set({ isViewingContractList: isViewingContractList }),

      isViewingExtension: false,
      setIsViewingExtension: isViewingExtension => set({ isViewingExtension: isViewingExtension }),

      isViewingAllocation: false,
      setIsViewingAllocation: isViewingAllocation =>
        set({ isViewingAllocation: isViewingAllocation }),

      //--------------------------------------------------------------------------
      //pointEditor state to trigger updates in other components
      includeAllPoints: false,
      setIncludeAllPoints: includeAllPoints => set({ includeAllPoints: includeAllPoints }),

      isViewingColorEditor: false,
      setIsViewingColorEditor: isViewingColorEditor =>
        set({ isViewingColorEditor: isViewingColorEditor }),

      showLineMeasurements: false,
      setShowLineMeasurements: showLineMeasurements =>
        set({ showLineMeasurements: showLineMeasurements }),

      showPointMeasurements: true,
      setShowPointMeasurements: showPointMeasurements =>
        set({ showPointMeasurements: showPointMeasurements }),

      // Interaction tracker for PointEditor
      hasPointEditorInteraction: false,
      setHasPointEditorInteraction: hasInteracted =>
        set({ hasPointEditorInteraction: hasInteracted }),

      // Run started tracker
      hasActiveRunStarted: false,
      setHasActiveRunStarted: started => set({ hasActiveRunStarted: started }),

      // Active run tracker
      hasActiveRun: false,
      setHasActiveRun: active => set({ hasActiveRun: active }),

      isInsertingPoint: false,
      setIsInsertingPoint: isInsertingPoint => set({ isInsertingPoint }),

      isEditingPointFillColor: false,
      isEditingPointBorderColor: false,
      isEditingLineColor: false,
      setIsEditingPointStyle: ({
        isEditingPointFillColor,
        isEditingPointBorderColor,
        isEditingLineColor,
      }) =>
        set({
          isEditingPointFillColor,
          isEditingPointBorderColor,
          isEditingLineColor,
        }),

      backgroundColor: '#BD10E0',
      setBackgroundColor: color => set({ backgroundColor: color }),

      //Assembly window states-----------------------------------------------------
      showAssemblyWindow: false,
      setShowAssemblyWindow: show => set({ showAssemblyWindow: show }),

      pointIsSelected: false,
      setPointIsSelected: pointIsSelected => set({ pointIsSelected: pointIsSelected }),

      selectedTotalLength: 0,
      setSelectedTotalLength: length => set({ selectedTotalLength: length }),

      //--------------------------------------------------------------------------

      numPages: 1,
      setNumPages: numPages => set({ numPages: numPages }),

      currentPage: 1,
      setCurrentPage: currentPage => set({ currentPage: currentPage }),

      isOpeningViewport: false,
      setIsOpeningViewport: isOpeningViewport => set({ isOpeningViewport: isOpeningViewport }),

      selectedObject: null,
      setSelectedObject: selected => set({ selectedObject: selected ?? null }),

      isCreatingNewProject: false,
      setIsCreatingNewProject: creatingNewProject =>
        set({ isCreatingNewProject: creatingNewProject }),

      pdfDimensions: { width: 0, height: 0 },
      setPdfDimensions: dimensions => set({ pdfDimensions: dimensions }),

      //Tool bar states for the document viewer------------------------------------
      userScale: 0,
      setUserScale: scale => set({ userScale: scale }),

      // Lasso selection
      isLassoMode: false,
      setIsLassoMode: on => set({ isLassoMode: on }),
      isLassoDrawing: false,
      setIsLassoDrawing: on => set({ isLassoDrawing: on }),
      lassoPoints: [],
      setLassoPoints: points =>
        set(state => ({
          lassoPoints: typeof points === 'function' ? points(state.lassoPoints) : points,
        })),

      measuredPoints: [],
      setMeasuredPoints: pointsOrUpdater =>
        set(state => ({
          measuredPoints:
            typeof pointsOrUpdater === 'function'
              ? pointsOrUpdater(state.measuredPoints)
              : pointsOrUpdater,
        })),

      temporaryPoints: [],
      setTemporaryPoints: points =>
        set(state => ({
          temporaryPoints: typeof points === 'function' ? points(state.temporaryPoints) : points,
        })),

      //annotating here is for takeoff assemblies - not the new annotation system
      isAnnotatingTakeoff: false,
      setIsAnnotatingTakeoff: isAnnotatingTakeoff => set({ isAnnotatingTakeoff }),

      //--------------------------------------------------------------------------

      //Assembly window states-----------------------------------------------------

      isCreatingTakeoffAssembly: false,
      setIsCreatingTakeoffAssembly: isCreatingTakeoffAssembly =>
        set({ isCreatingTakeoffAssembly: isCreatingTakeoffAssembly }),

      isUpdatingTakeoffAssembly: false,
      setIsUpdatingTakeoffAssembly: isUpdatingTakeoffAssembly =>
        set({ isUpdatingTakeoffAssembly: isUpdatingTakeoffAssembly }),

      isCounting: false,
      setIsCounting: isCounting => set({ isCounting: isCounting }),

      isGettingLength: false,
      setIsGettingLength: isGettingLength => set({ isGettingLength: isGettingLength }),

      isGettingArea: false,
      setIsGettingArea: isGettingArea => set({ isGettingArea: isGettingArea }),

      //--------------------------------------------------------------------------

      cursorPosition: { x: 0, y: 0 },
      setCursorPosition: position => set({ cursorPosition: position }),

      isDragging: false,
      setIsDragging: isDragging => set({ isDragging }),

      isEditingPoint: false,
      isEditingStyle: false,
      isDeletingPoint: false,
      assemblyIndex: null,
      pointIndex: 0,
      lineIndex: 0,
      editingPointX: null,
      editingPointY: null,

      setIsEditingPoint: ({
        isEditingPoint,
        isEditingStyle,
        isViewingTakeoffEditor,
        assemblyIndex,
        pointIndex,
        lineIndex,
        x,
        y,
      }) =>
        set(state => ({
          ...state,
          isEditingPoint,
          isEditingStyle: isEditingStyle !== undefined ? isEditingStyle : state.isEditingStyle,
          isViewingTakeoffEditor: isViewingTakeoffEditor,
          assemblyIndex: assemblyIndex ?? state.assemblyIndex,
          pointIndex: pointIndex ?? state.pointIndex,
          lineIndex: lineIndex ?? state.lineIndex,
          editingPointX: x ?? state.editingPointX,
          editingPointY: y ?? state.editingPointY,
        })),

      selectedPointsData: {
        selectedPoints: [],
        hasCopiedItems: false,
      },

      selectedEstimateIdForChangeOrder: null,
      setSelectedEstimateIdForChangeOrder: id => set({ selectedEstimateIdForChangeOrder: id }),

      // Project Hierarchy state
      expandedHierarchyNodes: new Set<string>(),
      setExpandedHierarchyNodes: (nodes: Set<string>) => set({ expandedHierarchyNodes: nodes }),
      toggleHierarchyNode: (nodeId: string) =>
        set(state => {
          const newSet = new Set(state.expandedHierarchyNodes);
          if (newSet.has(nodeId)) {
            newSet.delete(nodeId);
          } else {
            newSet.add(nodeId);
          }
          return { expandedHierarchyNodes: newSet };
        }),
      clearExpandedHierarchyNodes: () => set({ expandedHierarchyNodes: new Set() }),
    }),
    {
      name: 'unity-build-store',
      partialize: state => ({
        expandedHierarchyNodes: Array.from(state.expandedHierarchyNodes),
      }),
      onRehydrateStorage: () => state => {
        if (state?.expandedHierarchyNodes) {
          state.expandedHierarchyNodes = new Set(state.expandedHierarchyNodes);
        }
      },
    }
  )
);
